import { useCallback, useMemo, useRef, useState } from 'react';

import qs from 'qs';

import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePsfSharePageData, usePsfSharePageState } from '#src/app/pages/psf/share/pageContext';
import { usePromoteSlideUpExperiment } from '#src/app/pages/psf/share/shared/hooks';
import { hardNavigate } from '#src/app/pages/psf/share/shared/utils';
import { useIsMountedRef, useOnBfcacheRestore } from '#src/app/shared/hooks/lifecycle';
import { convertToUsdString } from '#src/app/shared/utils/currency';

import { useChipInAmount } from './useChipInAmount';

type Result = ModelHookResult<
	{
		isFooterTapHandledInPage: boolean;
		isEnabled: boolean;
		isOpen: boolean;
		isPending: boolean;
		amountLocalized: string;
	},
	{
		handleFooterTap: () => Promise<void>;
		handleOpenChange: (open: boolean) => void;
		handleChipIn: () => void;
		handleSkip: () => void;
	}
>;

/** Event properties and the promote destination, both derived from the chip-in amount. */
function useTapTargets(chipInAmount: number, chipInCurrencyCode: string, chipInExchangeRate: number) {
	const {
		data: {
			petition,
			experiments: { revenueSharePromoteSlideUpExperiment },
		},
	} = usePsfSharePageData();
	const [{ shared }] = usePsfSharePageState();

	const properties = useMemo(
		() => ({
			context: 'psf_combo_share_message',
			trigger: shared ? 'continue' : 'skip',
			amount: convertToUsdString(chipInAmount, chipInExchangeRate),
			local_amount: chipInAmount,
			currency: chipInCurrencyCode,
			exchange_rate: chipInExchangeRate,
			experiment_variation: revenueSharePromoteSlideUpExperiment.variation,
		}),
		[shared, chipInAmount, chipInCurrencyCode, chipInExchangeRate, revenueSharePromoteSlideUpExperiment.variation],
	);

	const promoteUrl = useMemo(
		() =>
			`/p/${encodeURIComponent(petition.slug)}/promote${qs.stringify(
				{ source_location: 'psf_share_promote_slide_up', psf_combo_static_amount: chipInAmount },
				{ addQueryPrefix: true },
			)}`,
		[petition.slug, chipInAmount],
	);

	return { properties, promoteUrl };
}

/**
 * Every exit of the footer flow has spent a one-shot latch by the time it can throw, so a throw has
 * to end in a plain location change to wherever that exit was taking the supporter.
 */
function bailOut(errorReporter: UtilityContext['errorReporter'], reason: string, url: string, error: unknown): void {
	void errorReporter.report({ error: error as Error, context: { reason } });
	hardNavigate(url);
}

type Track = ReturnType<typeof useTracking>;

/**
 * The sheet's two exits. Both buttons navigate, so the first one pressed is the last thing that
 * happens on this page and a second press would only duplicate the event; `resetSheetExits` re-arms
 * that latch after a bfcache restore.
 */
function useLeaveSheet({
	claimNavigation,
	track,
	properties,
	navigate,
	errorReporter,
}: {
	claimNavigation: () => boolean;
	track: Track;
	properties: ReturnType<typeof useTapTargets>['properties'];
	navigate: ReturnType<typeof useNavigate>;
	errorReporter: UtilityContext['errorReporter'];
}) {
	const hasTakenSheetActionRef = useRef(false);

	const leaveSheet = useCallback(
		(reason: string, eventName: Parameters<Track>[0], url: string) => {
			if (hasTakenSheetActionRef.current) return;
			hasTakenSheetActionRef.current = true;

			try {
				claimNavigation();
				void track(eventName, properties);
				navigate(url);
			} catch (error) {
				bailOut(errorReporter, reason, url, error);
			}
		},
		[errorReporter, claimNavigation, track, properties, navigate],
	);

	const resetSheetExits = useCallback(() => {
		hasTakenSheetActionRef.current = false;
	}, []);

	return { leaveSheet, resetSheetExits };
}

/**
 * The whole footer tap for the Second Promote Ask R1 promote slide-up: the legacy footer event,
 * treatment, and whichever of "open the sheet" or "navigate on" follows.
 *
 * `isFooterTapHandledInPage` means the tap is intercepted for both arms — the control supporter has
 * to stay on the page long enough to be treated, so the footer cancels the link and navigates
 * itself once treatment resolves. `isEnabled` (variant only) says whether to render the sheet.
 *
 * Every direct gesture here acts: it records a navigation claim so the automatic share redirect
 * stands down, but never defers to one, because the supporter pressed the control. The in-flight
 * ref is what keeps a single tap from being handled twice.
 *
 * The sheet is one-shot: once dismissed, a second footer tap navigates on rather than reopening it,
 * so the supporter is never trapped on the share page.
 *
 * `skipUrl` is the footer's own next step, reused as the slide-up's skip destination.
 */
export function usePromoteSlideUp(skipUrl: string): Result {
	const {
		data: { initial },
	} = usePsfSharePageData();
	const { chipInAmount, chipInAmountLocalized, chipInCurrencyCode, chipInExchangeRate } = useChipInAmount();
	const [{ shared }] = usePsfSharePageState();
	const track = useTracking();
	const navigate = useNavigate();
	const { errorReporter } = useUtilityContext();
	const { arm, awaitTreatment, claimNavigation, hasShownSheet, markSheetShown, restoreEpoch } =
		usePromoteSlideUpExperiment();
	const [isOpen, setOpen] = useState(false);
	const [isPending, setPending] = useState(false);
	const tapInFlightRef = useRef(false);
	const isMountedRef = useIsMountedRef();
	const { properties, promoteUrl } = useTapTargets(chipInAmount, chipInCurrencyCode, chipInExchangeRate);
	const { leaveSheet, resetSheetExits } = useLeaveSheet({
		claimNavigation,
		track,
		properties,
		navigate,
		errorReporter,
	});

	// The provider clears the page-scoped flow on a bfcache restore; this state lives in the
	// component, and would otherwise come back mid-tap with a sheet whose buttons no longer act.
	useOnBfcacheRestore(
		useCallback(() => {
			resetSheetExits();
			tapInFlightRef.current = false;
			setOpen(false);
			setPending(false);
		}, [resetSheetExits]),
	);

	const showSheetOrNavigate = useCallback(() => {
		if (arm === 'variant' && !hasShownSheet()) {
			markSheetShown();
			setOpen(true);
			void track('combo_psf_promote_slide_up_opened', properties);

			return;
		}

		navigate(skipUrl);
	}, [arm, hasShownSheet, markSheetShown, track, properties, navigate, skipUrl]);

	return {
		data: {
			isFooterTapHandledInPage: arm !== 'ineligible',
			isEnabled: arm === 'variant',
			isOpen,
			isPending,
			amountLocalized: chipInAmountLocalized,
		},
		actions: {
			handleFooterTap: useCallback(async () => {
				if (tapInFlightRef.current) return;
				tapInFlightRef.current = true;

				try {
					void track(shared ? 'combo_psf_continue' : 'combo_psf_skip', {
						context: initial ? 'psf_combo_share_initial' : 'psf_combo_share_message',
					});

					if (arm === 'ineligible') return;

					// Claimed before the wait, not after: the share redirect can be waiting on the same treatment,
					// and it must stand down the moment the supporter has tapped, not once treatment resolves.
					claimNavigation();
					const epoch = restoreEpoch();
					setPending(true);
					await awaitTreatment();

					// A restore while waiting means the supporter left and came back; that tap is over.
					if (isMountedRef.current && restoreEpoch() === epoch) showSheetOrNavigate();
				} catch (error) {
					bailOut(errorReporter, 'psf_share_promote_slide_up_tap', skipUrl, error);
				} finally {
					tapInFlightRef.current = false;
					if (isMountedRef.current) setPending(false);
				}
			}, [
				arm,
				initial,
				shared,
				awaitTreatment,
				claimNavigation,
				restoreEpoch,
				showSheetOrNavigate,
				errorReporter,
				track,
				skipUrl,
				isMountedRef,
			]),
			handleOpenChange: useCallback((open: boolean) => setOpen(open), []),
			handleChipIn: useCallback(
				() =>
					leaveSheet('psf_share_promote_slide_up_sheet_chip_in', 'combo_psf_promote_slide_up_cta_click', promoteUrl),
				[leaveSheet, promoteUrl],
			),
			handleSkip: useCallback(
				() => leaveSheet('psf_share_promote_slide_up_sheet_skip', 'combo_psf_promote_slide_up_skip', skipUrl),
				[leaveSheet, skipUrl],
			),
		},
	};
}
